import clsx from 'clsx';
import { type RefObject, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AI_QUOTA_USAGE_STATUS, useAIQuota } from 'core/hooks/useAIQuota';

import { type QuotaFirstUseNotice, getQuotaFirstUseNotice } from '../../../utils/quotaFirstUseNotice';

type Params = {
  /** False once the student closed the toast, and while the chat is open. */
  canShowFirstUseNotice: boolean;
  onFirstUseNoticeDismiss: () => void;
};

type FirstUseNotice = QuotaFirstUseNotice & {
  closeLabel: string;
};

type ChatTriggerButtonView = {
  ariaLabel: string;
  buttonRef: RefObject<HTMLButtonElement>;
  className: string;
  /** Texts of the first-use toast, or null when it is not shown. */
  firstUseNotice: FirstUseNotice | null;
  onFirstUseNoticeClose: () => void;
};

/**
 * Follows the quota the provider already reads on mount: the robot must not fetch it itself, or
 * opening the chat would read it three times.
 */
function useChatTriggerButton({ canShowFirstUseNotice, onFirstUseNoticeDismiss }: Params): ChatTriggerButtonView {
  const { t } = useTranslation(['chatbot']);
  const { usage } = useAIQuota();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isDisconnected = usage?.status === AI_QUOTA_USAGE_STATUS.EXHAUSTED;
  const isFirstUseNoticeShown = canShowFirstUseNotice && usage?.status === AI_QUOTA_USAGE_STATUS.UNUSED;

  // The close button disappears with the toast: without this, focus would fall back to the body.
  const onFirstUseNoticeClose = useCallback((): void => {
    buttonRef.current?.focus();
    onFirstUseNoticeDismiss();
  }, [onFirstUseNoticeDismiss]);

  return {
    ariaLabel: isDisconnected ? t('chatbot:open_chat_assistant_quota_exhausted') : t('chatbot:Open chat assistant'),
    buttonRef,
    className: clsx('chatbot-button', { 'chatbot-button--disconnected': isDisconnected }),
    firstUseNotice: isFirstUseNoticeShown ? { ...getQuotaFirstUseNotice(t, usage.quota), closeLabel: t('chatbot:quota_first_use_close') } : null,
    onFirstUseNoticeClose,
  };
}

export default useChatTriggerButton;
